import React, {Component} from 'react';
import moment from 'moment';

import {tryGetTestsForClass} from 'utils/testUtils';
import {createResults} from 'utils/resultsUtils';

import {
	updateStage,
	updateReady,
	subscribeToRoomConfig,
	joinRoom,
	submitTestResults,
	startTimer,
	endTimer,
	subscribeToClientPing,
	subscribeToMasterPong,
	subscribeToServerPong,
	clientPong,
	subscribeToRoomDeleted,
	subscribeToSocketLifecycle,
} from 'api';

import WorkingMemory from 'tests/WorkingMemory';
import RapidNaming from 'tests/RapidNaming';
import PhonemeIsolation from 'tests/PhonemeIsolation';
import PhonemeDeletion from 'tests/PhonemeDeletion';
import OneMinute from 'tests/OneMinute';
import LetterKnowledge from 'tests/LetterKnowledge';
import WordDictation from 'tests/WordDictation';
import Spoonerism from 'tests/Spoonerism';
import SentenceMemory from 'tests/SentenceMemory';
import NonwordRepetition from 'tests/NonwordRepetition';
import TestComplete from 'components/TestComplete';
import ProgressBar from 'components/ProgressBar';

import {tests} from 'constants/tests.js';

import './styles.scss';
import strings from 'strings';

const getTestFromName = (testName) => {
	switch (testName) {
		case 'LetterKnowledge':
			return LetterKnowledge;
		case 'Spoonerism':
			return Spoonerism;
		case 'WorkingMemory':
			return WorkingMemory;
		case 'RapidNaming':
			return RapidNaming;
		case 'PhonemeDeletion':
			return PhonemeDeletion;
		case 'PhonemeIsolation':
			return PhonemeIsolation;
		case 'OneMinute':
			return OneMinute;
		case 'WordDictation':
			return WordDictation;
		case 'SentenceMemory':
			return SentenceMemory;
		case 'NonwordRepetition':
			return NonwordRepetition;
		default:
			return TestComplete;
	}
};

const getDisplayTestName = (testName) =>
	strings['testName_' + testName] || testName;

class TestUser extends Component {
	constructor(props) {
		super(props);
		this.expectedRoomKey = String(props.roomKey || '').toUpperCase();
		this.state = {
			roomConfig: {},
			currentStep: 0,
			currentStage: 0,
			currentTest: 0,
			finished: false,
			finishedReason: null,
			disconnected: false,
		};
		this._unsubRoomDeleted = null;
		this._onRoomConfig = null;
		this._rejoinTimeout = null;
	}

	componentDidMount() {
		this._onRoomConfig = (roomConfig) => {
			if (!roomConfig || typeof roomConfig !== 'object') return;
			const incomingKey = String(
				(roomConfig.key || roomConfig.testKey || '')
			).toUpperCase();
	if (incomingKey && incomingKey !== this.expectedRoomKey) {
				console.warn(
					'[TestUser] Ignoring room config for unexpected key',
					incomingKey,
					'expected',
					this.expectedRoomKey
				);
				return;
			}

    if (roomConfig.language && 
		roomConfig.language !== strings.getLanguage()) {
      strings.setLanguage(roomConfig.language);
    }

			this.setState({
				roomConfig,
				currentTest: roomConfig.currentTest || 0,
				currentStage: roomConfig.currentStage || 0,
				currentStep: roomConfig.currentStep || 0,
				stepData: roomConfig.stepData,
			});
		};

		this._unsubRoomConfig = subscribeToRoomConfig(this._onRoomConfig);

		this._unsubRoomDeleted = subscribeToRoomDeleted(({reason}) => {
			this.setState({
				finished: true,
				finishedReason: reason || 'finished',
				roomConfig: null,
				currentTest: 0,
				currentStage: 0,
				currentStep: 0,
			});
			setTimeout(function () {
				window.location.href = '/';
			}, 2000);
		});

		 this._unsubServerPong = subscribeToServerPong((payload) => {
			this.setState({
				lastServerPong: moment(),
			});
		});

		this._unsubMasterPong = subscribeToMasterPong((payload) => {
			this.setState({
				lastClientPong: moment(),
			});
		});

		this._unsubClientPing = subscribeToClientPing((payload) => {
			clientPong(payload);
		});

		this._unsubLifecycle = subscribeToSocketLifecycle({
			onDisconnect: () => {
				// cancel any in-progress rejoin retry so a fresh cycle starts on next reconnect
				if (this._rejoinTimeout) {
					clearTimeout(this._rejoinTimeout);
					this._rejoinTimeout = null;
				}
				this.setState({disconnected: true});
			},
			onReconnect: () => {
				const startTime = Date.now();
				const attemptRejoin = () => {
					joinRoom(this.props.roomKey, this.props.roomSecret, 'testUser')
						.then((roomConfig) => {
							// connection restored — hide the banner
							this.setState({disconnected: false});
							var shouldMarkReady =
								!roomConfig ||
								roomConfig.currentTest == null ||
								roomConfig.currentTest === 0 ||
								roomConfig.currentStage === 0;
							if (shouldMarkReady) {
								updateReady(true);
							}
						})
						.catch(() => {
							// keep retrying every 5 seconds for up to 1 minute
							if (Date.now() - startTime < 60000) {
								this._rejoinTimeout = setTimeout(attemptRejoin, 5000);
							} else {
								this.setState({
									finished: true,
									finishedReason: 'rejoin failed',
									roomConfig: null,
									currentTest: 0,
									currentStage: 0,
									currentStep: 0,
								});
							}
						});
				};
				attemptRejoin();
			},
		});

joinRoom(this.props.roomKey, this.props.roomSecret, 'testUser')
	.then((roomConfig) => {
		var shouldMarkReady =
			!roomConfig ||
			roomConfig.currentTest == null ||
			roomConfig.currentTest === 0 ||
			roomConfig.currentStage === 0;

		if (shouldMarkReady) {
			updateReady(true);
		}
	})
	.catch((err) => {
		console.warn('[TestUser] joinRoom failed:', err);
		alert(typeof err === 'string' ? err : 'Could not join test session');
		this.props.onSelectRole('initial');
	});
	}

	componentWillUnmount() {
		if (this._rejoinTimeout) clearTimeout(this._rejoinTimeout);
		if (this._unsubRoomDeleted) this._unsubRoomDeleted();
		if (this._unsubRoomConfig) this._unsubRoomConfig();
		if (this._unsubServerPong) this._unsubServerPong();
		if (this._unsubMasterPong) this._unsubMasterPong();
		if (this._unsubClientPing) this._unsubClientPing();
		if (this._unsubLifecycle) this._unsubLifecycle();
	}

	nextTest = (results) => {
		const {currentTest} = this.state;
		if (results) this.submitResults(results);

		if (currentTest > tests.length - 1) {
		} else {
			const newTest = currentTest + 1;
			this.setState({
				currentTest: newTest,
				currentStep: 0,
				currentStage: 0,
			});
			updateStage(newTest, 0, 0);
		}
	};

	nextStep = () => {
		const {
			currentStep: step,
			currentStage: stage,
			currentTest: test,
		} = this.state;
		endTimer(test, stage, step);
		const newStep = step + 1;
		updateStage(test, stage, newStep);
		this.setState({currentStep: newStep});
	};

	nextStage = () => {
		const {
			currentStage: stage,
			currentTest: test,
		} = this.state;
		if (stage !== 0) return;

		const newStage = stage + 1;
		updateStage(test, newStage, 0);
		this.setState({currentStage: newStage, currentStep: 0});
	};

	setReady = (isReady) => {
		updateReady(isReady);
	};

	submitResults = (answers, vars = null, timers = null) => {
		const {roomConfig} = this.state;

		const {testId, results} = createResults(
			roomConfig,
			answers,
			vars,
			timers
		);

		if (
			['youth-followup', 'adult-followup', 'child-followup'].includes(
				this.props.testKeyType
			)
		)
			submitTestResults(testId, results)
				.then((res) => {
					console.log('Results submitted!');
					// show success message
				})
				.catch((err) => {
					console.error('Submit failed:', err.data);
					alert(err.data.message); // the backend error message
				});
		else
			submitTestResults(testId, results, roomConfig.candidateInfo.adminId)
				.then((res) => {
					console.log('Results submitted!');
					// show success message
				})
				.catch((err) => {
					console.error('Submit failed:', err.data);
					alert(err.data.message); // the backend error message
				});
	};

	submitClientResults = (answers) => {
		const {currentTest} = this.state;
		updateStage(currentTest, 2, 0);
		this.submitResults(answers);
	};
	startTimer = () => {
		const {
			currentTest: test,
			currentStage: stage,
			currentStep: step,
		} = this.state;
		startTimer(test, stage, step);
	};
	endTimer = () => {
		const {
			currentTest: test,
			currentStage: stage,
			currentStep: step,
		} = this.state;
		endTimer(test, stage, step);
	};
	render() {
		const {roomConfig, currentTest, finished, disconnected} = this.state;
		if (finished) {
			return (
				<div className="TestUser">
					<p className="testKey">{this.props.roomKey}</p>
					<div className="TestUserIntro">
						<p>
							{strings.test_session_ended ||
								'Denne testøkten er avsluttet.'}
						</p>
					</div>
				</div>
			);
		}

		let StageComponent = null;
		let isTest = false;
		let currentSubTest = null;
		let testNumbersArray = [];

		if (roomConfig && currentTest !== 0 && roomConfig.ageGroup != null) {
			const testType =
				roomConfig.candidateInfo && roomConfig.candidateInfo.testType;
			const testNumbers = tryGetTestsForClass(
				roomConfig.ageGroup,
				testType || this.props.testKeyType
			);
			testNumbersArray = testNumbers ? Object.entries(testNumbers) : [];
			currentSubTest = testNumbers ? testNumbers[currentTest] : null;

			const test = currentSubTest ? getTestFromName(currentSubTest) : null;
			isTest = Boolean(test);
			if (isTest) {
				StageComponent = test;
			}
		}
		const shouldShowProgressBar = Boolean(
			roomConfig &&
				isTest &&
				currentSubTest &&
				testNumbersArray.length
		);

		return (
			<div className="TestUser">
				{shouldShowProgressBar && (
					<ProgressBar
						completedCount={Number(currentTest)}
						totalCount={testNumbersArray.length - 1}
						currentLabel={getDisplayTestName(currentSubTest)}
					/>
				)}
				<p className="testKey">{this.props.roomKey}</p>
				{disconnected && (
					<div className="TestUser__reconnecting">
						<div className="TestUser__reconnecting__card">
							<div className="TestUser__reconnecting__spinner" />
							<p className="TestUser__reconnecting__title">Tilkoblingen er tapt</p>
							<p className="TestUser__reconnecting__subtitle">Prøver å koble til igjen…</p>
						</div>
					</div>
				)}
				{(!isTest || currentTest === 0) && (
					<div className="TestUserIntro">
						<p>{strings.waitforadminstart}</p>
					</div>
				)}
				{isTest && currentTest !== 0 && (
					<div className="test">
						<StageComponent
							soundPool={this.props.soundPool}
							currentTest={roomConfig.currentTest}
							currentStage={roomConfig.currentStage}
							currentStep={roomConfig.currentStep}
							stepData={roomConfig.stepData}
							nextStep={this.nextStep}
							nextStage={this.nextStage}
							ageGroup={roomConfig.ageGroup}
							startTimer={this.startTimer}
							endTimer={this.endTimer}
							testKeyType={
								roomConfig.candidateInfo.testType ||
								this.props.testKeyType
							}
							submitWordDictation={this.submitClientResults}
							nextTest={this.nextTest}
							submitPhonemeIsolation={this.submitClientResults}
							setReady={this.setReady}
							isCandidate={true}
						/>
					</div>
				)}
			</div>
		);
	}
}

export default TestUser;
